import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Shield, Clock, Download, Eye, FileText, Copy, Check,
  AlertTriangle, Ban,
} from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ============================================================
   Branded share-link portal — /r/:token
   - Unauthenticated, tokenized access
   - Mobile-first, restrained industrial aesthetic
   - Renders one of: ok | expired | revoked | not_found
   ============================================================ */
export default function SharePortal() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/share/${token}/meta`);
        if (alive) setState({ loading: false, ...data });
      } catch (e) {
        if (alive) {
          if (e?.response?.status === 404) setState({ loading: false, status: "not_found" });
          else setState({ loading: false, status: "error", error: e?.message });
        }
      }
    })();
    return () => { alive = false; };
  }, [token]);

  if (state.loading) {
    return <PortalShell><LoadingBlock /></PortalShell>;
  }
  if (state.status === "not_found") {
    return <PortalShell><InvalidState /></PortalShell>;
  }
  if (state.status === "expired") {
    return <PortalShell tenant={state.tenant}><ExpiredState expiresAt={state.expires_at} /></PortalShell>;
  }
  if (state.status === "revoked") {
    return <PortalShell tenant={state.tenant}><RevokedState /></PortalShell>;
  }
  return (
    <PortalShell tenant={state.tenant}>
      <ReportPreview token={token} meta={state} />
    </PortalShell>
  );
}

/* ============================================================
   Portal Shell — full-bleed, restrained industrial.
   Pulls tenant primary color from snapshot when available.
   ============================================================ */
function PortalShell({ tenant, children }) {
  const primary = tenant?.primary_color || "#e85d1f";
  const poweredBy = tenant?.powered_by_forgedops ?? true;
  const footerText = tenant?.footer_text || "Powered by ForgedOps™";

  return (
    <div
      className="min-h-screen text-[var(--fop-text)]"
      style={{
        backgroundColor: "#0d0e12",
        "--fop-primary": primary,
      }}
      data-testid="share-portal"
    >
      <div className="h-1" style={{ background: `linear-gradient(90deg, ${primary} 0%, transparent 100%)` }} />
      <main className="max-w-3xl mx-auto px-5 sm:px-8 pt-12 sm:pt-16 pb-20">
        {children}
      </main>
      <footer className="border-t border-white/5 py-6 px-5 text-center">
        {poweredBy && (
          <div className="text-[10px] uppercase tracking-[0.18em] text-white/35" data-testid="share-footer">
            {footerText}
          </div>
        )}
      </footer>
    </div>
  );
}

/* ============================================================
   States
   ============================================================ */
function LoadingBlock() {
  return (
    <div className="animate-pulse space-y-4">
      <div className="h-3 w-32 bg-white/5 rounded" />
      <div className="h-10 w-3/4 bg-white/5 rounded" />
      <div className="h-3 w-1/2 bg-white/5 rounded" />
      <div className="h-40 w-full bg-white/5 rounded mt-8" />
    </div>
  );
}

function InvalidState() {
  return (
    <div className="text-center py-10" data-testid="share-invalid">
      <Ban size={36} className="text-white/30 mx-auto mb-5" />
      <h1 className="text-xl font-semibold text-white/90">Link not found</h1>
      <p className="text-sm text-white/45 mt-2 max-w-md mx-auto">
        This share link is invalid or has been removed. Please request a new link from your project team.
      </p>
    </div>
  );
}

function ExpiredState({ expiresAt }) {
  const when = new Date(expiresAt).toLocaleString();
  return (
    <div className="text-center py-10" data-testid="share-expired">
      <Clock size={36} className="text-white/30 mx-auto mb-5" />
      <h1 className="text-xl font-semibold text-white/90">This link has expired</h1>
      <p className="text-sm text-white/45 mt-2 max-w-md mx-auto">
        Access ended on <span className="text-white/70">{when}</span>. Please request a refreshed link from your project team.
      </p>
    </div>
  );
}

function RevokedState() {
  return (
    <div className="text-center py-10" data-testid="share-revoked">
      <AlertTriangle size={36} className="text-white/30 mx-auto mb-5" />
      <h1 className="text-xl font-semibold text-white/90">Link revoked</h1>
      <p className="text-sm text-white/45 mt-2 max-w-md mx-auto">
        This share link has been revoked by the project administrator. Please request a new link to access this report.
      </p>
    </div>
  );
}

/* ============================================================
   Main preview — ok state
   ============================================================ */
function ReportPreview({ token, meta }) {
  const { tenant, project, report, health, narrative, expires_at, download_count } = meta;
  const primary = tenant?.primary_color || "#e85d1f";
  const downloadHref = `${process.env.REACT_APP_BACKEND_URL}/api/share/${token}`;
  const logoSrc = tenant?.logo_url
    ? `${process.env.REACT_APP_BACKEND_URL}/api/share/${token}/logo`
    : null;
  const [copied, setCopied] = useState(false);
  const expiry = useExpiry(expires_at);
  const reportSize = useMemo(() => formatBytes(report?.size_bytes || 0), [report]);
  const generatedAt = useMemo(
    () => report?.generated_at ? new Date(report.generated_at).toLocaleString() : "—",
    [report]
  );

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  return (
    <div className="space-y-10">
      {/* Brand header */}
      <header className="flex items-center gap-3" data-testid="share-brand-header">
        {logoSrc ? (
          <img src={logoSrc} alt="" className="h-8 w-auto object-contain" data-testid="share-tenant-logo" />
        ) : (
          <div className="w-8 h-8 rounded grid place-items-center font-bold text-xs" style={{ backgroundColor: primary }}>
            {(tenant?.display_name || "F").slice(0, 1)}
          </div>
        )}
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-white/55 font-semibold">
            {tenant?.display_name || "ForgedOps Plans"}
          </div>
          <div className="text-[10px] text-white/30">Secure document share</div>
        </div>
        <div className="ml-auto flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-white/45 font-mono" data-testid="share-secure-indicator">
          <Shield size={12} /> Tokenized
        </div>
      </header>

      {/* Report identity */}
      <section>
        <div className="text-[11px] uppercase tracking-[0.22em] font-bold mb-3" style={{ color: primary }}>
          {report?.kind_label || "Coordination Report"}
        </div>
        <h1 className="text-3xl sm:text-4xl font-semibold text-white leading-tight" data-testid="share-project-name">
          {project?.name}
        </h1>
        <div className="text-sm text-white/55 mt-2 font-mono">
          {[project?.number, project?.client, project?.location].filter(Boolean).join("  ·  ")}
        </div>
      </section>

      {/* Operational summary card */}
      {(health?.score != null || narrative) && (
        <section
          className="rounded border border-white/5 p-5 sm:p-6"
          style={{ background: "rgba(255,255,255,0.02)" }}
          data-testid="share-summary-card"
        >
          {health?.score != null && (
            <div className="flex items-baseline gap-5 mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-mono">Coordination Health</div>
                <div className="flex items-baseline gap-3 mt-1">
                  <div className="text-4xl font-bold" style={{ color: health?.status?.color || primary }} data-testid="share-health-score">
                    {health.score}
                  </div>
                  <div className="text-xs uppercase tracking-wider font-semibold" style={{ color: health?.status?.color || primary }}>
                    {health?.status?.label || "—"}
                  </div>
                </div>
              </div>
              {health?.top_reasons?.length > 0 && (
                <div className="flex-1 min-w-0 pl-5 border-l border-white/5">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-mono mb-1.5">Top Reasons</div>
                  <ul className="space-y-1" data-testid="share-top-reasons">
                    {health.top_reasons.slice(0, 3).map((r, i) => (
                      <li key={i} className="text-xs text-white/70 flex items-start gap-2">
                        <span className="font-mono text-white/40 shrink-0" style={{ color: primary }}>−{r.penalty}</span>
                        <span className="truncate">{r.label}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          {narrative && (
            <div className="pt-4 border-t border-white/5">
              <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-mono mb-2">Executive Summary</div>
              <p className="text-sm text-white/80 leading-relaxed" data-testid="share-narrative">
                {narrative}
              </p>
            </div>
          )}
        </section>
      )}

      {/* Document delivery panel */}
      <section className="rounded border border-white/5 overflow-hidden" data-testid="share-delivery-panel">
        <div className="px-5 py-4 border-b border-white/5 flex items-center gap-3" style={{ background: "rgba(255,255,255,0.02)" }}>
          <FileText size={18} className="text-white/60 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm text-white/90 font-medium truncate" data-testid="share-filename">
              {report?.filename}
            </div>
            <div className="text-[11px] text-white/40 font-mono">
              PDF · {reportSize}{report?.generated_by_name ? ` · Generated by ${report.generated_by_name}` : ""}
            </div>
          </div>
        </div>
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <a
            href={downloadHref}
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-center gap-2 px-4 py-3 rounded font-medium text-sm transition-opacity hover:opacity-90"
            style={{ backgroundColor: primary, color: "#0d0e12" }}
            data-testid="share-download-btn"
          >
            <Download size={16} /> Download report
          </a>
          <a
            href={downloadHref}
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-center gap-2 px-4 py-3 rounded font-medium text-sm text-white/80 border border-white/10 hover:bg-white/5 transition-colors"
            data-testid="share-preview-btn"
          >
            <Eye size={16} /> Preview in browser
          </a>
        </div>
      </section>

      {/* Meta grid */}
      <section className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-[11px] font-mono" data-testid="share-meta-grid">
        <MetaCell label="Generated" value={generatedAt} />
        <MetaCell
          label="Access expires"
          value={expiry.label}
          accent={expiry.urgency === "soon" ? primary : null}
          testid="share-expiry"
        />
        <MetaCell label="Downloads" value={download_count ?? 0} />
      </section>

      {/* Copy link */}
      <button
        onClick={copyLink}
        className="mx-auto flex items-center gap-2 px-4 py-2 rounded border border-white/10 text-xs text-white/55 hover:bg-white/5 transition-colors"
        data-testid="share-copy-btn"
      >
        {copied ? <Check size={12} /> : <Copy size={12} />}
        {copied ? "Copied to clipboard" : "Copy share link"}
      </button>
    </div>
  );
}

function MetaCell({ label, value, accent, testid }) {
  return (
    <div className="border-t border-white/5 pt-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mb-1">{label}</div>
      <div className="text-sm text-white/85" style={accent ? { color: accent } : undefined}>{value}</div>
    </div>
  );
}

/* ============================================================
   Helpers
   ============================================================ */
function formatBytes(n) {
  if (!n) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n < 10 ? 1 : 0)} ${units[i]}`;
}

function useExpiry(expiresAt) {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);
  if (!expiresAt) return { label: "—", urgency: "none" };
  const expiry = new Date(expiresAt);
  const ms = expiry.getTime() - Date.now();
  if (ms <= 0) return { label: "Expired", urgency: "expired" };
  const hours = ms / 3_600_000;
  if (hours < 1) return { label: `In ${Math.max(1, Math.round(ms / 60_000))}m`, urgency: "soon" };
  if (hours < 24) return { label: `In ${Math.round(hours)}h`, urgency: "soon" };
  const days = Math.floor(hours / 24);
  const remHours = Math.round(hours - days * 24);
  return {
    label: `In ${days}d${remHours ? ` ${remHours}h` : ""}`,
    urgency: days <= 1 ? "soon" : "ok",
  };
}
